// The welcome checklist panel under a product (AdminProducts.dc.html): numbered steps, each with Move
// up and Remove, and a box to add 1. The board edits words only, so a new step is a plain tick for
// everyone; an existing step's button and who it shows to (The Funding Engine's only while an intake
// runs) are kept, and shown under it (the map's recommendation). Writes are direct, owner and admin.
import { useState } from 'react'
import { Button } from '../ui/index.js'
import { CHECKLIST } from './copy.js'
import { moveUpChanges, nextSortOrder } from './model.js'
import { addStep, reorderSteps, removeStep } from './data.js'
import { Mark } from './bits.jsx'

// A step's words with {group} and {next_session}, which each member reads as their own (Join the Group 2
// WhatsApp), shown as what they stand for; the step keeps them as written
const PLACEHOLDER = /\{(group|next_session)\}/g
export const readable = body => String(body || '').replace(PLACEHOLDER, (_, key) => CHECKLIST.placeholders[key])
function StepWords({ body }) {
  const parts = String(body || '').split(PLACEHOLDER)
  return parts.map((part, i) => (i % 2
    ? <span key={i} className="ap-step-ph">{CHECKLIST.placeholders[part]}</span>
    : part))
}

export function ChecklistPanel({ api, product, items, onChange, id }) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function run(fn) {
    if (busy) return
    setBusy(true)
    setError(null)
    try { await fn() } catch (e) { setError(e.message || String(e)) } finally { setBusy(false) }
  }

  const add = e => {
    e.preventDefault()
    const body = text.trim()
    if (!body) return
    if (body.length > 200) { setError(CHECKLIST.tooLong); return }
    run(async () => {
      const row = await addStep(api, product.id, body, nextSortOrder(items))
      onChange([...items, row])
      setText('')
    })
  }
  const up = i => run(async () => {
    const changes = moveUpChanges(items, i)
    await reorderSteps(api, changes)
    const order = new Map(changes.map(c => [c.id, c.sort_order]))
    onChange(items.map(it => (order.has(it.id) ? { ...it, sort_order: order.get(it.id) } : it)))
  })
  const remove = item => run(async () => {
    await removeStep(api, item.id)
    onChange(items.filter(it => it.id !== item.id))
  })

  return (
    <div className="ap-panel" id={id}>
      <p className="ap-panel-line">{CHECKLIST.line(product.name)}</p>
      {items.length > 0 && (
        <ol className="ap-steps">
          {items.map((item, i) => (
            <li className="ap-step" key={item.id}>
              <span className="ap-num" aria-hidden="true">{i + 1}</span>
              <span className="ap-step-text">
                <StepWords body={item.body} />
                {(item.action_kind !== 'none' && item.action_label) || item.audience !== 'everyone' ? (
                  <span className="ap-step-note">
                    {[item.action_kind !== 'none' && item.action_label ? CHECKLIST.button(item.action_label) : null,
                      item.audience === 'running_intake' ? CHECKLIST.runningIntake : item.audience === 'no_running_intake' ? CHECKLIST.noRunningIntake : null]
                      .filter(Boolean).join('. ')}
                  </span>
                ) : null}
              </span>
              <button type="button" className="ap-icon-btn" aria-label={CHECKLIST.moveUp(readable(item.body))} disabled={i === 0 || busy} onClick={() => up(i)}>
                <Mark name="up" />
              </button>
              <button type="button" className="ap-text-btn" disabled={busy} onClick={() => remove(item)}>{CHECKLIST.remove}</button>
            </li>
          ))}
        </ol>
      )}
      {items.length === 0 && <p className="ap-panel-quiet">{CHECKLIST.empty}</p>}
      <form className="ap-add-step" onSubmit={add}>
        <input className="ad-input ap-add-input" aria-label={CHECKLIST.newLabel} placeholder={CHECKLIST.placeholder} value={text}
          maxLength={200} onChange={e => setText(e.target.value)} />
        <Button kind="ink" size="sm" type="submit" busy={busy}>{CHECKLIST.add}</Button>
      </form>
      <p className="ap-step-hint">{CHECKLIST.placeholdersHint}</p>
      {error && <p className="ad-error" role="alert">{error}</p>}
    </div>
  )
}
